import React from 'react';
import { CommonResources } from '../../../config/CommonResources';

export interface IExploreSearchProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

/**
 * Search field for the Explore catalog. The caller owns the focus shortcut (the "/" key) through the forwarded input ref;
 * the "/" hint is shown while the field is empty and unfocused.
 */
const ExploreSearch = React.forwardRef<HTMLInputElement, IExploreSearchProps>(function ExploreSearch(props, ref) {
  const innerRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Escape') return;
    if (props.value) props.onChange('');
    else innerRef.current?.blur();
  };

  return (
    <div className={`explore-search${props.className ? ` ${props.className}` : ''}`}>
      <svg className="explore-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        ref={innerRef}
        type="text"
        className="explore-search-input"
        placeholder={CommonResources.Search}
        aria-label={CommonResources.Search}
        aria-keyshortcuts="/"
        autoComplete="off"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {props.value ? (
        <button type="button" className="explore-search-clear" aria-label="Clear search" onClick={() => { props.onChange(''); innerRef.current?.focus(); }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      ) : (
        <kbd className="explore-search-hint" aria-hidden="true">
          /
        </kbd>
      )}
    </div>
  );
});

export default ExploreSearch;
